import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreationInfo,
  ItemViewModel,
  UnidentifiedCreationInfo,
} from "../../../generated/data-contracts";
import { CreationType, CreationTypes } from "../../../models";
import {
  AuthorsUrls,
  AuthorsUtils,
  ErrorUtils,
  IAxiosError,
  api,
} from "../../../utils";
import { AvisForm } from "../../shared/components";
import { AvisPrimaryButton } from "../../shared/components/buttons";
import {
  AvisSection,
  AvisTitleAndSubtitle,
  AvisToastMessageInfo,
} from "../../shared/components/form";
import { AvisConfirmWithCommentModal } from "../../shared/components/modal/AvisConfirmWithCommentModal";
import { AvisContext } from "../../shared/context/AvisContext";
import { ErrorLoadingDataHoc } from "../../shared/error/ErrorLoadingDataHoc";
import { CreationNewPicker } from "../creations/creationPicker/CreationNewPicker";
import { CreationPicker } from "../creations/creationPicker/CreationPicker";
import { UnidentifiedCreationCommonData } from "./components/UnidentifiedCreationCommonData";

export function UnidentifiedCreationViewPage() {
  const { res, ctxRightsHolder } = useContext(AvisContext);

  const [unidentifiedCreation, setUnidentifiedCreation] = useState<
    UnidentifiedCreationInfo | undefined
  >();
  const [selectedCreation, setSelectedCreation] = useState<
    CreationInfo | undefined
  >();
  const [createdNewCreationInfo, setCreatedNewCreationInfo] = useState<
    ItemViewModel | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<IAxiosError>();
  const [isCreationSearchMode, setIsCreationSearchMode] =
    useState<boolean>(false);
  const [isCreationNewMode, setIsCreationNewMode] = useState<boolean>(false);
  const [isConfirm1SearchOpen, setIsConfirm1SearchOpen] =
    useState<boolean>(false);
  const [isConfirm2CreatedNewOpen, setIsConfirm2CreatedNewOpen] =
    useState<boolean>(false);
  const [isConfirm3RejectOpen, setIsConfirm3RejectOpen] =
    useState<boolean>(false);
  const [
    isSubmittingUnidentifiedResponse,
    setIsSubmittingUnidentifiedResponse,
  ] = useState<boolean>(false);

  const navigate = useNavigate();

  const urlParams = useMemo(
    () => AuthorsUtils.getUrlParams(ctxRightsHolder),
    [ctxRightsHolder],
  );

  const pageTitleValue = useMemo(() => {
    if (isLoading) return;

    return unidentifiedCreation?.creationName;
  }, [isLoading, unidentifiedCreation]);

  const getGridData = useCallback(() => {
    if (
      !ctxRightsHolder?.rightsHolderId ||
      !urlParams.unidentifiedCreationProcessId
    )
      return;

    setIsLoading(true);

    api
      .rightsHoldersGetUnidentifiedCreation(
        ctxRightsHolder.rightsHolderId,
        urlParams.unidentifiedCreationProcessId,
      )
      .then((res) => setUnidentifiedCreation(res.data))
      .catch(setError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [ctxRightsHolder, urlParams]);

  useEffect(() => {
    getGridData();
  }, [getGridData]);

  const creationClassNotSupported = useMemo(() => {
    const creationClassCode = unidentifiedCreation?.creationClass?.code ?? "";
    return !CreationTypes.includes(creationClassCode);
  }, [unidentifiedCreation]);

  const handleThisIsNotMyCreation = useMemo(
    () => (comment: string | undefined) => {
      if (
        !ctxRightsHolder?.rightsHolderId ||
        !urlParams.unidentifiedCreationProcessId
      )
        return;

      setIsSubmittingUnidentifiedResponse(true);
      api
        .rightsHoldersPostUnidentifiedCreationStatus(
          ctxRightsHolder.rightsHolderId,
          urlParams.unidentifiedCreationProcessId,
          { belongsToRightsHolder: false, notes: comment },
        )
        .then(() => {
          ErrorUtils.showSuccessfulMessage(
            res("message_Success"),
            res("authors_UnidentifiedCreations_message_ResponseSent"),
            10,
          );
          navigate(
            AuthorsUtils.getUrlWithParams(
              AuthorsUrls.rightsHoldersCreationUnidentifiedList,
              {
                rightsHolderId: ctxRightsHolder?.rightsHolderId,
                rightsHolderTypeCode: ctxRightsHolder?.rightsHolderTypeCode,
              },
            ),
          );
        });
    },
    [
      ctxRightsHolder?.rightsHolderId,
      ctxRightsHolder?.rightsHolderTypeCode,
      navigate,
      res,
      urlParams.unidentifiedCreationProcessId,
    ],
  );

  const handleSearchCreationCallback = useMemo(
    () => (comment: string | undefined) => {
      if (
        !selectedCreation?.creationId ||
        !ctxRightsHolder ||
        !urlParams.unidentifiedCreationProcessId
      )
        return;

      setIsSubmittingUnidentifiedResponse(true);
      return AuthorsUtils.handleFoundOrCreatedCreation(
        ctxRightsHolder,
        urlParams.unidentifiedCreationProcessId,
        comment,
        selectedCreation.creationId,
        false,
      )
        .then(() => {
          ErrorUtils.showSuccessfulMessage(
            res("message_Success"),
            res("authors_UnidentifiedCreations_message_CreationLinked"),
            10,
          );
          navigate(
            AuthorsUtils.getUrlWithParams(
              AuthorsUrls.rightsHoldersCreationUnidentifiedList,
              {
                rightsHolderId: ctxRightsHolder?.rightsHolderId,
                rightsHolderTypeCode: ctxRightsHolder?.rightsHolderTypeCode,
              },
            ),
          );
        })
        .catch((err) => ErrorUtils.handleAxiosErrorResponse(err, res));
    },
    [
      ctxRightsHolder,
      navigate,
      res,
      selectedCreation?.creationId,
      urlParams.unidentifiedCreationProcessId,
    ],
  );

  const handleCreateNewCreationCallback = useMemo(
    () => (comment: string | undefined) => {
      if (
        !createdNewCreationInfo?.id ||
        !ctxRightsHolder ||
        !urlParams.unidentifiedCreationProcessId
      )
        return;

      setIsSubmittingUnidentifiedResponse(true);
      return AuthorsUtils.handleFoundOrCreatedCreation(
        ctxRightsHolder,
        urlParams.unidentifiedCreationProcessId,
        comment,
        createdNewCreationInfo.id,
        true,
      )
        .then(() => {
          ErrorUtils.showSuccessfulMessage(
            res("message_Success"),
            res("authors_UnidentifiedCreations_message_CreationLinked"),
            10,
          );
          navigate(
            AuthorsUtils.getUrlWithParams(
              AuthorsUrls.rightsHoldersCreationUnidentifiedList,
              {
                rightsHolderId: ctxRightsHolder?.rightsHolderId,
                rightsHolderTypeCode: ctxRightsHolder?.rightsHolderTypeCode,
              },
            ),
          );
        })
        .catch((err) => ErrorUtils.handleAxiosErrorResponse(err, res));
    },
    [
      createdNewCreationInfo?.id,
      ctxRightsHolder,
      navigate,
      res,
      urlParams.unidentifiedCreationProcessId,
    ],
  );

  const mainFormClassName = useMemo(
    () => (isCreationSearchMode ? "hidden" : ""),
    [isCreationSearchMode],
  );

  const renderActions = useCallback(
    () => (
      <div className="unidentified-creation-actions">
        <AvisPrimaryButton
          label={res("authors_UnidentifiedCreations_action_SearchExisting")}
          onClick={() => setIsCreationSearchMode(true)}
          disabled={creationClassNotSupported}
        />
        <AvisPrimaryButton
          label={res("authors_UnidentifiedCreations_action_CreateNew")}
          onClick={() => setIsCreationNewMode(true)}
          disabled={creationClassNotSupported}
        />
        <AvisPrimaryButton
          label={res("authors_UnidentifiedCreations_action_Reject")}
          onClick={() => setIsConfirm3RejectOpen(true)}
        />
      </div>
    ),
    [creationClassNotSupported, res],
  );

  const renderModalWindows = useMemo(
    () => (
      <>
        {isCreationSearchMode && (
          <CreationPicker
            defaultCreationTypeId={unidentifiedCreation?.creationClass?.id}
            onClose={() => {
              setIsCreationSearchMode(false);
              setSelectedCreation(undefined);
            }}
            onConfirm={() => setIsConfirm1SearchOpen(true)}
            selectedCreation={selectedCreation}
            setSelectedCreation={setSelectedCreation}
          />
        )}
        {isCreationNewMode && (
          <CreationNewPicker
            defaultCreationType={
              (unidentifiedCreation?.creationClass?.code as CreationType) ?? "M"
            }
            onClose={() => {
              setIsCreationNewMode(false);
              setCreatedNewCreationInfo(undefined);
            }}
            onConfirm={() => setIsConfirm2CreatedNewOpen(true)}
            createdNewCreationInfo={createdNewCreationInfo}
            setCreatedNewCreationInfo={setCreatedNewCreationInfo}
          />
        )}
        {(isConfirm1SearchOpen || isConfirm2CreatedNewOpen) && (
          <AvisConfirmWithCommentModal
            title={res("authors_UnidentifiedCreations_Process_Modal_Title")}
            contentText={res(
              "authors_UnidentifiedCreations_Process_Modal_label_Recognize",
              unidentifiedCreation?.creationName ?? "",
            )}
            disabled={isSubmittingUnidentifiedResponse}
            isOpen={isConfirm1SearchOpen || isConfirm2CreatedNewOpen}
            onConfirm={
              isConfirm1SearchOpen
                ? handleSearchCreationCallback
                : handleCreateNewCreationCallback
            }
            onClose={() =>
              isConfirm1SearchOpen
                ? setIsConfirm1SearchOpen(false)
                : setIsConfirm2CreatedNewOpen(false)
            }
          />
        )}
        {isConfirm3RejectOpen && (
          <AvisConfirmWithCommentModal
            title={res("authors_UnidentifiedCreations_Process_Modal_Title")}
            contentText={res(
              "authors_UnidentifiedCreations_Process_Modal_label_Reject",
            )}
            disabled={isSubmittingUnidentifiedResponse}
            isOpen={isConfirm3RejectOpen}
            onConfirm={handleThisIsNotMyCreation}
            onClose={() => setIsConfirm3RejectOpen(false)}
          />
        )}
      </>
    ),
    [
      createdNewCreationInfo,
      handleCreateNewCreationCallback,
      handleSearchCreationCallback,
      handleThisIsNotMyCreation,
      isConfirm1SearchOpen,
      isConfirm2CreatedNewOpen,
      isConfirm3RejectOpen,
      isCreationNewMode,
      isCreationSearchMode,
      isSubmittingUnidentifiedResponse,
      res,
      selectedCreation,
      unidentifiedCreation,
    ],
  );

  return (
    <>
      <AvisForm
        pageTitle={pageTitleValue}
        className={mainFormClassName}
        backLink={{
          title: res("label_Back"),
          url: AuthorsUtils.getUrlWithParams(
            AuthorsUrls.rightsHoldersCreationUnidentifiedList,
            urlParams,
          ),
        }}
      >
        <ErrorLoadingDataHoc error={error} loading={isLoading}>
          <AvisTitleAndSubtitle
            title={unidentifiedCreation?.creationName}
            subTitle={`${res("label_CreationClassName")}: ${unidentifiedCreation
              ?.creationClass?.name}`}
          />
          <UnidentifiedCreationCommonData
            unidentifiedCreation={unidentifiedCreation}
          />
          <AvisSection
            title={res("authors_UnidentifiedCreations_SectionTitle")}
            textToDisplayKey="portal_RH_UnidentifiedCreationCard"
          >
            <AvisToastMessageInfo
              className="mb-3"
              visible={creationClassNotSupported}
              textKey="portal_RH_UnidentifiedCreationUnsupportedType"
            />
            {renderActions()}
          </AvisSection>
        </ErrorLoadingDataHoc>
      </AvisForm>
      {renderModalWindows}
    </>
  );
}
